import { get } from 'lodash';

import { config, msg } from 'api';
import errorHandler from 'lib/utils/errorHandler';
import { setErrorMessage, setSuccessMessage } from 'apps/notifications/actions/notifications';

import { ApiRedirectCausedRejection } from 'utils/network';

export const UPDATE_REVIEW_FIELDS = 'UPDATE_REVIEW_FIELDS';
export const ADD_REVIEWS = 'ADD_REVIEWS';
export const REMOVE_REVIEW = 'REMOVE_REVIEW';
export const ADD_REVIEW_SUMMARY = 'ADD_REVIEW_SUMMARY';
export const NO_OF_REVIEWS_ON_PAGE = 6;
export const SET_ACTIVE_PAGE = 'SET_ACTIVE_PAGE';
export const RESET_ACTIVE_PAGE = 'RESET_ACTIVE_PAGE';
export const DEFAULT_SORT_ORDER = 'MOST_RECENT';
export const POST_REVIEW = 'POST_REVIEW';
export const UPDATE_REVIEW = 'UPDATE_REVIEW';
export const REVIEW_CHANGE_STEP = 'REVIEW_CHANGE_STEP';
export const SET_REVIEW_VALIDATION_ERRORS = 'SET_REVIEW_VALIDATION_ERRORS';
export const SET_REVIEW_MODAL_VISIBILITY = 'SET_REVIEW_MODAL_VISIBILITY';
export const SET_SORT_ORDER = 'SET_SORT_ORDER';

const NICKNAME_ERRORS = {
  NICKNAME_ALREADY_SET: 'Nickname already set',
  NICKNAME_ALREADY_EXISTS: 'Sorry, that nickname’s already taken. Please use a different one.',
  NICKNAME_REJECTED: 'Nickname rejected',
  NICKNAME_NOT_FOUND: 'Nickname has not been found',
};

const errorMessageByCode = msg.reviews.validationErrors;

export function addReviews(reviews) {
  return {
    type: ADD_REVIEWS,
    reviews,
  };
}

export function updateReview(reviewId, isHelpful) {
  return {
    type: UPDATE_REVIEW,
    reviewId,
    isHelpful,
  };
}

export function addReviewSummary(reviewSummary) {
  return {
    type: ADD_REVIEW_SUMMARY,
    reviewSummary,
  };
}

export function removeReview(reviewId) {
  return {
    type: REMOVE_REVIEW,
    reviewId,
  };
}

export function updateFields(fields) {
  return {
    type: UPDATE_REVIEW_FIELDS,
    fields,
  };
}
export function resetActivePage() {
  return {
    type: RESET_ACTIVE_PAGE,
  };
}

export function setActivePage(activePageDelta) {
  return {
    type: SET_ACTIVE_PAGE,
    activePageDelta,
  };
}

export function setSortOrder(sortOrder) {
  return {
    type: SET_SORT_ORDER,
    sortOrder,
  };
}

export function displayAddDetailsForm() {
  return {
    type: REVIEW_CHANGE_STEP,
    step: 'addDetails',
  };
}

export function setValidationErrors(validationErrors) {
  return {
    type: SET_REVIEW_VALIDATION_ERRORS,
    validationErrors,
  };
}

export function setModalVisibility(modalVisible) {
  return {
    type: SET_REVIEW_MODAL_VISIBILITY,
    modalVisible,
  };
}

export function fetchReviews(sku, offset = 0, limit = NO_OF_REVIEWS_ON_PAGE, sortOrder) {
  return (dispatch, getState, { network }) => {
    const storedSortOrder = sortOrder || getState().reviews.sortOrder;
    return network.get(
      `${config.apiBasePath}/products/${sku}/reviews?sortOrder=${storedSortOrder}&limit=${limit}&offset=${offset}`)
      .then(data => {
        dispatch(addReviews(data));
      })
      .catch(errorHandler);
  };
}

export function fetchReviewSummary(sku) {
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/products/${sku}/reviews/summary`)
      .then(data => dispatch(addReviewSummary(data)))
      .catch((err) => {
        if (err.status === 500) {
          errorHandler(err);
        }
        return dispatch(addReviewSummary({}));
      });
}

export function postReview(sku, reviewData) {
  return (dispatch, getState, { network }) =>
    network.post(
      `${config.apiBasePath}/products/${sku}/reviews`,
      {
        body: JSON.stringify(reviewData),
      })
      .then(() => dispatch(setSuccessMessage('Review added successfully')))
      .then(() => dispatch(fetchReviews(
        sku,
        (getState().reviews.activePage - 1) * NO_OF_REVIEWS_ON_PAGE || 0,
        NO_OF_REVIEWS_ON_PAGE,
        getState().reviews.sortOrder
      )))
      .then(() => dispatch(fetchReviewSummary(sku)))
      .catch((err) => {
        const { data: { code, fieldErrors = [], message } = {} } = err;
        if (code === 'VALIDATION_ERROR') {
          const errors = fieldErrors.map(fieldError => ({
            ...fieldError,
            message: get(errorMessageByCode, `${fieldError.field}.${fieldError.code}`, fieldError.message),
          }));
          dispatch(setValidationErrors(errors));
          return false;
        }
        return dispatch(setErrorMessage(message));
      });
}

export function validateNickname(nickname) {
  return (dispatch, getState, { network }) =>
    network.post(
      `${config.apiBasePath}/nickname/applicable`,
      {
        body: JSON.stringify({ nickname }),
      })
      .then(() => true)
      .catch((err) => {
        const { data: { code, fieldErrors = [], message } = {} } = err;
        if (code) {
          let errorMessage = NICKNAME_ERRORS[code] || 'Error';
          if (code === 'VALIDATION_ERROR') {
            const { code: errorCode } = fieldErrors[0];
            errorMessage = errorMessageByCode.nickname[errorCode] || fieldErrors[0].message;
          }
          dispatch(setValidationErrors([{ field: 'newNickname', message: errorMessage, code }]));
          return false;
        }
        dispatch(setErrorMessage(message));
        return false;
      });
}

export function deleteReview(sku, reviewId) {
  return (dispatch, getState, { network }) =>
    network.del(
      `${config.apiBasePath}/products/${sku}/reviews/${reviewId}`)
      .then(() => dispatch(removeReview(reviewId)))
      .then(() => dispatch(setSuccessMessage('Review removed successfully')))
      .then(() => {
        // Ensure that active page is not bigger than last page:
        let activePage = Math.min(
          Math.ceil((getState().reviews.totalCount - 1) / NO_OF_REVIEWS_ON_PAGE),
          (getState().reviews.activePage)
        );
        // now ensure that it's not smaller than the first page:
        activePage = Math.max(activePage, 1);
        const activePageDelta = getState().reviews.activePage === activePage ? 0 : -1;
        dispatch(setActivePage(activePageDelta));
      })
      .then(() => dispatch(fetchReviews(
        sku,
        (getState().reviews.activePage - 1) * NO_OF_REVIEWS_ON_PAGE,
        NO_OF_REVIEWS_ON_PAGE,
        getState().reviews.sortOrder
      )))
      .then(() => dispatch(fetchReviewSummary(sku)))
      .catch((err) => {
        const { message } = err.data || {};
        dispatch(setErrorMessage(message));
        return err.data;
      });
}

export function setHelpfulness(sku, reviewId, isHelpful) {
  return (dispatch, getState, { network }) =>
    network.put(
      `${config.apiBasePath}/products/${sku}/reviews/${reviewId}/votes`,
      {
        body: JSON.stringify({ helpful: isHelpful }),
      })
      .then(() => dispatch(updateReview(reviewId, isHelpful)))
      .catch((err) => {
        if (!(err instanceof ApiRedirectCausedRejection)) {
          dispatch(setErrorMessage('Most probably you have rated this review already'));
        }
        return err.data;
      });
}
